/**
 * @file `useAuthStore` — Pinia store tracking the signed-in user across both Firebase Auth
 * (`firebaseUser`) and the Firestore profile document (`dbUser`). Persisted across reloads
 * via `pinia-plugin-persistedstate` and mirrored to IndexedDB so the user keeps their
 * profile in offline mode.
 *
 * Use this store anywhere the UI needs the *actual* user identity or profile; for a simple
 * "is anyone signed in?" check prefer the lighter `useAuthState` composable.
 */

import { defineStore } from 'pinia'
import { type User as FirebaseUser } from 'firebase/auth'
import { type User as DbUser } from '@/types/Types'
import authService from '@/stores/authService'
import { saveUserProgress, loadUserProgress } from '@/utils/offlineUserProgress'
import { getAdditionnalUserInfo } from '@/stores/users'

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    firebaseUser: null,
    dbUser: null
  }),
  persist: true,

  getters: {
    isAuthenticated: (state) => !!state.firebaseUser
  },

  actions: {
    /**
     * Boots the store: resolves the current Firebase Auth user, then hydrates `dbUser`
     * from the Firestore profile. On Firestore error (typically offline) falls back to the
     * IndexedDB-cached profile. Idempotent — safe to re-invoke after sign-in transitions.
     */
    getSignedInUser: async (state: AuthState) => {
      state.firebaseUser = await authService.getSignedInUser()

      if (state.firebaseUser) {
        try {
          state.dbUser = await getAdditionnalUserInfo(state.firebaseUser.uid)
          if (state.dbUser) {
            await saveUserProgress(state.dbUser)
          }
        } catch (e) {
          console.error('Error fetching user info:', e)
          // If offline or error, try to load from local storage
          const offlineUser = await loadUserProgress(state.firebaseUser.uid)
          if (offlineUser) {
            state.dbUser = offlineUser
          }
        }
      }
    },
    /**
     * Re-fetches `dbUser` after a profile mutation (display name, photo, language, …) so
     * the rest of the UI sees the change without remounting. Same offline fallback path as
     * `getSignedInUser`.
     */
    updateDbUser: async (state: AuthState) => {
      if (state.firebaseUser) {
        try {
          state.dbUser = await getAdditionnalUserInfo(state.firebaseUser.uid)
          if (state.dbUser) {
            await saveUserProgress(state.dbUser)
          }
        } catch (e) {
          console.error('Error updating user info:', e)
          // If offline or error, try to load from local storage
          const offlineUser = await loadUserProgress(state.firebaseUser.uid)
          if (offlineUser) {
            state.dbUser = offlineUser
          }
        }
      }
    }
  }
})

interface AuthState {
  firebaseUser: FirebaseUser | null
  dbUser: DbUser | null
}
